import { format, parseISO, addDays, startOfWeek, endOfWeek } from "date-fns";
import { monthGoalSnapshot } from "./goals";
import {
  WEEK_OPTIONS,
  callDateKey,
  daysInPeriod,
  eachDayKeys,
  elapsedDaysInPeriod,
  filterCallsByPeriod,
  monthKey,
  ymd,
} from "./period";

export function summarize(calls) {
  const totalSec = (calls || []).reduce((sum, call) => sum + (call.durationSec || 0), 0);
  const earnings = (calls || []).reduce((sum, call) => sum + (Number(call.earnings) || 0), 0);
  const count = (calls || []).length;
  return {
    calls: count,
    minutes: totalSec / 60,
    hours: totalSec / 3600,
    earnings,
    avgSec: count ? Math.round(totalSec / count) : 0,
    totalSec,
  };
}

export function money(n, symbol = "$") {
  return `${symbol}${(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function minutesLabel(n) {
  return (n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export function hms(totalSec) {
  const sec = Math.max(0, Math.round(totalSec || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function currencySymbol(calls) {
  return calls?.[0]?.currencySymbol || "$";
}

export function companyName(call) {
  return call?.companyName || call?.platform || "Unassigned";
}

export function breakdownByCompany(calls) {
  const map = new Map();
  (calls || []).forEach((call) => {
    const name = companyName(call);
    const row = map.get(name) || { name, calls: 0, minutes: 0, earnings: 0 };
    row.calls += 1;
    row.minutes += (call.durationSec || 0) / 60;
    row.earnings += Number(call.earnings) || 0;
    map.set(name, row);
  });
  return [...map.values()].sort((a, b) => b.earnings - a.earnings);
}

export function workPace(calls, period) {
  const stats = summarize(calls);
  const elapsed = Math.max(1, elapsedDaysInPeriod(period));
  const hours = stats.hours;
  return {
    perHour: hours > 0 ? stats.earnings / hours : 0,
    perDay: stats.earnings / elapsed,
    minutesPerDay: stats.minutes / elapsed,
    callsPerDay: stats.calls / elapsed,
    hours,
    elapsed,
  };
}

export function monthlyProjection(periodCalls, period, monthlyGoal) {
  const stats = summarize(periodCalls);
  const elapsed = Math.max(1, elapsedDaysInPeriod(period));
  const span = Math.max(1, daysInPeriod(period));
  const month = monthKey(period.end);
  const daysInMonth = new Date(period.end.getFullYear(), period.end.getMonth() + 1, 0).getDate();
  const pacePerDay = stats.earnings / elapsed;
  const projectedMonth = pacePerDay * daysInMonth;
  const periodTarget = monthlyGoal > 0 ? (monthlyGoal / daysInMonth) * span : 0;
  return {
    month,
    earnings: stats.earnings,
    projectedMonth,
    monthlyGoal: monthlyGoal || 0,
    periodTarget,
    progress: monthlyGoal > 0 ? Math.min(1, stats.earnings / Math.max(periodTarget, 0.01)) : 0,
    monthProgress: monthlyGoal > 0 ? Math.min(1, projectedMonth / monthlyGoal) : 0,
    onTrack: monthlyGoal > 0 ? projectedMonth >= monthlyGoal : null,
  };
}

export function monthlyGoalSnapshot(calls, goal, now = new Date()) {
  return monthGoalSnapshot(calls, monthKey(now), now, goal);
}

export function earningsSeries(calls, period) {
  const span = daysInPeriod(period);
  if (span <= 1) {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({
      key: String(hour),
      label: `${String(hour).padStart(2, "0")}:00`,
      earnings: 0,
      minutes: 0,
      calls: 0,
    }));
    (calls || []).forEach((call) => {
      const hour = new Date(call.startTime || 0).getHours();
      const bucket = buckets[hour];
      if (!bucket) return;
      bucket.earnings += Number(call.earnings) || 0;
      bucket.minutes += (call.durationSec || 0) / 60;
      bucket.calls += 1;
    });
    return buckets;
  }

  const keys = eachDayKeys(period);
  const map = new Map(
    keys.map((key) => [
      key,
      {
        key,
        label: format(parseISO(`${key}T12:00:00`), span > 14 ? "MMM d" : "EEE d"),
        earnings: 0,
        minutes: 0,
        calls: 0,
      },
    ])
  );
  (calls || []).forEach((call) => {
    const key = callDateKey(call);
    const bucket = map.get(key);
    if (!bucket) return;
    bucket.earnings += Number(call.earnings) || 0;
    bucket.minutes += (call.durationSec || 0) / 60;
    bucket.calls += 1;
  });
  return [...map.values()];
}

export function weeklyIncomePerformance(allCalls, period) {
  const now = new Date();
  const inRange = now >= period.start && now <= period.end;
  const anchor =
    period.preset === "this_week" || period.preset === "last_week"
      ? period.start
      : inRange
        ? now
        : period.end;
  const start = startOfWeek(anchor, WEEK_OPTIONS);
  const end = endOfWeek(anchor, WEEK_OPTIONS);
  const prevStart = addDays(start, -7);
  const prevEnd = addDays(end, -7);
  const weekCalls = filterCallsByPeriod(allCalls, { start, end });
  const prevCalls = filterCallsByPeriod(allCalls, { start: prevStart, end: prevEnd });
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  function dayEarnings(list, day) {
    const key = ymd(day);
    return (list || []).reduce((sum, call) => {
      return callDateKey(call) === key ? sum + (Number(call.earnings) || 0) : sum;
    }, 0);
  }

  const series = days.map((label, i) => {
    const day = addDays(start, i);
    return {
      label,
      key: ymd(day),
      earnings: dayEarnings(weekCalls, day),
      prevWeek: dayEarnings(prevCalls, addDays(prevStart, i)),
    };
  });

  series.forEach((row, i) => {
    const prevDay = i === 0 ? null : series[i - 1].earnings;
    row.dropped = prevDay != null && prevDay > 0 && row.earnings < prevDay - 0.009;
    row.gained = prevDay != null && row.earnings > prevDay + 0.009;
  });

  const thisTotal = series.reduce((sum, row) => sum + row.earnings, 0);
  const prevTotal = series.reduce((sum, row) => sum + row.prevWeek, 0);
  let pctChange = null;
  if (prevTotal > 0) pctChange = ((thisTotal - prevTotal) / prevTotal) * 100;
  else if (thisTotal > 0) pctChange = 100;

  const highest = series.reduce((best, row) => (row.earnings > best.earnings ? row : best), series[0]);
  const lowest = series.reduce((best, row) => (row.earnings < best.earnings ? row : best), series[0]);
  const drops = series.filter((row) => row.dropped);

  return {
    label: `${format(start, "MMM d")} – ${format(end, "MMM d")}`,
    series,
    thisTotal,
    prevTotal,
    pctChange,
    highest,
    lowest,
    drops,
  };
}

